/**
 * Prosthodontic Vocabulary Dictionary
 * ---------------------------------------------------------------------------
 * Web Speech API (Chrome, Safari, Edge) uses general-purpose acoustic models
 * that routinely butcher prosthodontic vocabulary — "Ti-base" becomes
 * "tea base", "Ncm" becomes "N.C.M." or "in cm", "ISQ" becomes "I ask you",
 * "zirconia" becomes "circonia", etc.
 *
 * This dictionary maps common speech-recognition misreadings back to the
 * clinician's canonical spelling BEFORE the transcript reaches:
 *   • the ARIA intent router (so nav intents still match)
 *   • the /api/ask backend (so numerical-safety + PHI gates see the real term)
 *   • the on-screen "What ARIA heard" transcript
 *
 * Design notes:
 *   • Each entry is `{ canonical, variants, wholeWord? }`.
 *   • `variants` is an ordered list of case-insensitive fragments that will be
 *     matched against the raw transcript. First match wins per entry.
 *   • `wholeWord: false` allows sub-token replacements (e.g. "circonia" inside
 *     "layered circonia"); default true.
 *   • The engine (see ./index.js) applies replacements in dictionary order,
 *     so terms are ordered longest → shortest to prevent partial matches.
 *   • Adding a new term is safe: as long as its variants are specific enough
 *     to avoid collisions with everyday English, no engine change is needed.
 *
 * NOT a diagnostic tool. Purely lexical correction. No numerics, no phrases
 * that could be construed as clinical assertions.
 */

export const PROSTHODONTIC_TERMS = [
  // ─── Implant components ────────────────────────────────────────────────
  { canonical: "Ti-base", variants: ["tea base", "tea-base", "tee base", "tee-base", "ti base", "T base", "T-base"] },
  { canonical: "Titanium base", variants: ["titanium bass", "titanium-base"] },
  { canonical: "Multi-unit abutment", variants: ["multi unit abutment", "multiunit abutment", "multi-unit at abutment", "multiunit at bottom"] },
  { canonical: "Scan body", variants: ["scan bodies", "scanned body", "skin body"] },
  { canonical: "Emergence profile", variants: ["emergency profile", "emergent profile", "immersion profile"] },
  { canonical: "Healing abutment", variants: ["healing at abutment", "healing about it", "heeling abutment"] },
  { canonical: "Custom abutment", variants: ["custom at abutment", "custom at bottom", "customer abutment"] },
  { canonical: "Screw-retained crown", variants: ["screw retained crown", "screw-retained crown", "screw retained down"] },
  { canonical: "Cement-retained crown", variants: ["cement retained crown", "cement-retained crown", "cement retained down"] },
  { canonical: "Cover screw", variants: ["cover screw"] },
  { canonical: "Verification jig", variants: ["verification gig", "verification jigg"] },

  // ─── Occlusion & articulation ──────────────────────────────────────────
  { canonical: "Occlusal vertical dimension (OVD)", variants: ["occlusal vertical dimension"] },
  { canonical: "OVD", variants: ["oh vee dee", "O V D", "o.v.d.", "ovd"] },
  { canonical: "Centric Relation (CR)", variants: ["centric relation"] },
  { canonical: "Maximum Intercuspation (MIP)", variants: ["maximum intercuspation", "maximum inter-cuspation"] },
  { canonical: "MIP", variants: ["M I P", "m.i.p.", "em eye pee"] },
  { canonical: "Facebow", variants: ["face bow", "face-bow", "phase bow"] },
  { canonical: "Articulator", variants: ["articulate her", "articulate or", "artic-u-lator"] },
  { canonical: "Vertical dimension", variants: ["vertical diamension"] },

  // ─── Torque / stability metrics ────────────────────────────────────────
  { canonical: "Ncm", variants: ["N C M", "n.c.m.", "en see em", "and cm", "in cm", "newton centimeter", "newton centimetre", "newton centimeters", "newton centimetres", "N-cm", "N·cm"] },
  { canonical: "ISQ", variants: ["I ask you", "I S Q", "i.s.q.", "eye ess cue", "eye S Q", "is Q"] },

  // ─── Prosthesis classifications ────────────────────────────────────────
  { canonical: "FP1", variants: ["F P one", "F P 1", "F-P-1", "eff pee one", "F P-1"] },
  { canonical: "FP2", variants: ["F P two", "F P 2", "F-P-2", "eff pee two", "F P-2"] },
  { canonical: "FP3", variants: ["F P three", "F P 3", "F-P-3", "eff pee three", "F P-3"] },

  // ─── Materials ─────────────────────────────────────────────────────────
  { canonical: "PMMA", variants: ["P M M A", "p.m.m.a.", "pee em em ay"] },
  { canonical: "Monolithic zirconia", variants: ["monolithic circonia", "monolithic zirconium", "monolithic zerconia"] },
  { canonical: "Layered zirconia", variants: ["layered circonia", "layered zirconium", "layered zerconia"] },
  { canonical: "Zirconia", variants: ["circonia", "zirconium", "zerconia", "sir konia"] },
  { canonical: "Lithium disilicate", variants: ["lithium dysilicate", "lithium desilicate", "lithium disillicate", "lithium desalicate"] },
  { canonical: "e.max", variants: ["e max", "emacs", "e-max", "email X"] },
  { canonical: "Feldspathic", variants: ["felspathic", "feldspatic", "felds pathic"] },
  { canonical: "Cobalt-chromium", variants: ["cobalt chromium", "cobalt chrome"] },
  { canonical: "Composite resin", variants: ["composite resin"] },

  // ─── Prosthesis types ──────────────────────────────────────────────────
  { canonical: "Hybrid prosthesis", variants: ["hybrid prostheses", "hybrid prosthetic", "high-bred prosthesis", "hybrid procedures"] },
  { canonical: "Full-arch prosthesis", variants: ["full arch prosthesis", "full-arch prosthetic", "full arch prostheses", "full arch prosthetic"] },
  { canonical: "All-on-X", variants: ["all on X", "all on x", "all-on X", "all-on-x", "all-on ex", "all on ex"] },
  { canonical: "All-on-4", variants: ["all on four", "all on 4", "all-on-4", "all on-4", "all on four dental"] },
  { canonical: "All-on-6", variants: ["all on six", "all on 6", "all-on-6"] },
  { canonical: "Immediate loading", variants: ["immediate load", "immediate loading protocol"] },

  // ─── Implant systems (proper nouns — high mis-transcription rate) ──────
  { canonical: "Straumann BLX", variants: ["Straumann B L X", "Strauman BLX", "Strawman BLX", "Strauman B L X", "Strawman B L X"] },
  { canonical: "Straumann", variants: ["Strauman", "Strawman", "storman", "stroh man", "stroh-man"] },
  { canonical: "Nobel N1", variants: ["Nobel N one", "Nobel and one", "Noble N1", "Noble N one", "Noble and one"] },
  { canonical: "Nobel Biocare", variants: ["Nobel biocare", "Noble Biocare", "Noble biocare", "Nobel bio care", "Noble bio care"] },
  { canonical: "NobelProcera", variants: ["Nobel procera", "Nobel per sarah", "Nobel per sara", "Noble procera", "Nobel Procera"] },
  // Context-gated: "Conical Connection" the Nobel implant-abutment interface.
  // Speech recognisers often hear this as "chronicle connection" or
  // "chronic connection". Never rewrite outside an implant conversation —
  // "chronicle" and "chronic" are ordinary English words.
  { canonical: "Conical Connection", variants: ["chronicle connection", "chronical connection", "chronic connection", "conical connexion", "conical-connection", "canonical connection"],
    context: ["nobel", "implant", "abutment", "platform", "screw", "biocare", "procera"] },
  // Context-gated platforms — everyday English uses "RP", "NP", "WP",
  // "regular", "narrow", "wide" for many non-clinical things. Only rewrite
  // when the transcript already mentions an implant or a Nobel term.
  { canonical: "NP platform", variants: ["N P platform", "n.p. platform", "narrow platform", "N-P platform", "N P", "n p", "N.P.", "n.p.", "en pea", "en pee", "and pea", "and pee"],
    context: ["nobel", "implant", "abutment", "conical", "biocare", "procera", "screw", "torque", "platform"] },
  { canonical: "RP platform", variants: ["R P platform", "r.p. platform", "regular platform", "R-P platform", "R P", "r p", "R.P.", "r.p.", "are pea", "are pee", "our pea", "our pee", "raise the", "raise it", "arpee"],
    context: ["nobel", "implant", "abutment", "conical", "biocare", "procera", "screw", "torque", "platform"] },
  { canonical: "WP platform", variants: ["W P platform", "w.p. platform", "wide platform", "W-P platform", "W P", "w p", "W.P.", "w.p.", "double you pea", "double you pee", "wp platform"],
    context: ["nobel", "implant", "abutment", "conical", "biocare", "procera", "screw", "torque", "platform"] },
  // Screw types — these appear in general English (a "titanium screw" for
  // a laptop, "abutment" of a bridge, etc.). Gate on implant vocabulary.
  { canonical: "Titanium screw", variants: ["titanium a screw", "tie tanium screw", "titanium-screw", "Ti screw", "T I screw"],
    context: ["nobel", "implant", "abutment", "conical", "biocare", "procera", "torque"] },
  // Context-gated mobile-SR variants: iOS/Safari and Android Chrome
  // frequently mis-hear "abutment screw" as everyday English homophones
  // ("apartment", "abundant", "a button"). Only rewrite when the active
  // conversation already concerns implants / components / screws / torque
  // so a real "apartment screw" (e.g. a hardware discussion) is preserved.
  { canonical: "Abutment screw", variants: [
      "at bottom screw", "abutmen screw", "a butman screw", "abutmentscrew", "abut ment screw",
      "apartment screw", "apartments screw", "an apartment screw",
      "abundant screw", "abundance screw", "an abundant screw",
      "a button screw", "a buttons screw", "the button screw",
      "abetment screw", "abatement screw",
    ],
    context: ["nobel", "implant", "conical", "biocare", "procera", "torque", "platform", "straumann", "blx", "blt", "component"] },
  // Context-gated mobile-SR variants for the OCCLUSAL / PROSTHETIC screw
  // (Straumann's "occlusal screw" ≙ Nobel's "prosthetic screw" ≙ "crown
  // screw"). iOS Safari commonly hears "occlusal" as "close all" or
  // "closer" — 2026-02 founder mobile test caught the misroute
  // "close all screw value" → generic no-match. Only rewrite inside an
  // implant / screw / torque conversation so ordinary English is safe.
  { canonical: "Occlusal screw", variants: [
      "close all screw", "close all screw value", "closer screw", "closer screws",
      "clothes all screw", "close-all screw", "close-all-screw", "closes all screw",
      "occlusal-screw", "occlusalscrew", "occlusal screws",
      "closes screw", "close a screw",
    ],
    context: ["nobel", "implant", "conical", "biocare", "procera", "torque", "platform", "straumann", "blx", "blt", "abutment", "prosthetic", "crown", "restoration"] },
  { canonical: "Crown screw", variants: ["crowns screw", "crown-screw", "screw retained crown screw", "prosthetic screw"],
    context: ["nobel", "implant", "abutment", "conical", "biocare", "procera", "torque", "platform"] },
  { canonical: "Neodent", variants: ["neodent", "neo dent", "neo-dent", "neodont"] },
  { canonical: "Atlantis", variants: ["Atlantis abutment", "Atlantic", "Atlantis abutmen"] },
  { canonical: "BioHorizons", variants: ["Bio Horizons", "Bio-Horizons", "Bio horizons"] },
  { canonical: "Zimmer", variants: ["Zimmer implant", "Zimmerman implant"] },

  // ─── CAD/CAM & imaging ─────────────────────────────────────────────────
  { canonical: "exocad", variants: ["exo cad", "exo-cad", "exocard", "echo cad"] },
  { canonical: "Primescan", variants: ["prime scan", "prime-scan", "primes can"] },
  { canonical: "TRIOS", variants: ["Trios", "trios", "try os", "try-os", "T R I O S"] },
  { canonical: "CBCT", variants: ["C B C T", "c.b.c.t.", "see bee see tee", "cone beam CT", "cone-beam CT"] },
  { canonical: "DICOM", variants: ["D I C O M", "d.i.c.o.m.", "dye com", "dee com"] },
  { canonical: "STL", variants: ["S T L", "s.t.l.", "ess tee ell"] },
  { canonical: "PLY", variants: ["P L Y", "p.l.y."] },

  // ─── Common misrecognitions (dental anatomy / procedures) ──────────────
  { canonical: "Sinus lift", variants: ["sinus lifted", "sinus lyft"] },
  { canonical: "Ridge preservation", variants: ["ridge preservation"] },
  { canonical: "Osseointegration", variants: ["osteo integration", "osseo integration", "osteointegration"] },
  { canonical: "Bone graft", variants: ["bone graph", "bone crafts"] },
  { canonical: "Keratinized tissue", variants: ["carotene ice tissue", "keratinised tissue", "keratinized tissues"] },
  { canonical: "Attached gingiva", variants: ["attached gingival", "a-tached gingiva"] },
];
